import { defineStore } from "pinia";

import applicationsApiService from "@/services/modules/applications";

import type { EXTRA_DEAL_VIEW_REQUESTS } from "@/helpers/constants/deals";
import type {
  IDataOrchestrationLog,
  PhoneNumbersResponse,
} from "@/models/applications";

interface UseApplicationsState {
  allDataOrchestrationLogs: IDataOrchestrationLog[];
  dataOrchestrationLog: IDataOrchestrationLog | null;
  gettingOrchestrationLogs: boolean;
  canSeeDealActivity: boolean;
  shouldFetch: Record<(typeof EXTRA_DEAL_VIEW_REQUESTS)[number], boolean>;
  showVersionHistoryOverlay: boolean;
  phoneNumbers: PhoneNumbersResponse | null;
}

const getDefaultState = (): UseApplicationsState => ({
  allDataOrchestrationLogs: [],
  dataOrchestrationLog: null,
  gettingOrchestrationLogs: false,
  canSeeDealActivity: false,
  showVersionHistoryOverlay: false,
  shouldFetch: {
    notes: true,
    businessNotes: true,
    offers: true,
  },
  phoneNumbers: null,
});

const useApplicationsStore = defineStore("applications", {
  state: () => getDefaultState(),
  getters: {
    activeDataOrchestrationLog: ({ dataOrchestrationLog }) =>
      dataOrchestrationLog,
    shouldFetchNotes: ({ shouldFetch }) => shouldFetch.notes,
    shouldFetchBusinessNotes: ({ shouldFetch }) => shouldFetch.businessNotes,
    shouldFetchOffers: ({ shouldFetch }) => shouldFetch.offers,
    getLogsForStage:
      ({ allDataOrchestrationLogs }) =>
      (stageId: string, firstUnderwritingStageId: string | null) => {
        return allDataOrchestrationLogs.filter((log) => {
          return (
            log.stage_id === stageId ||
            (log.stage_id === null && firstUnderwritingStageId === stageId)
          );
        });
      },
  },
  actions: {
    async getLatestDataOrchestrationLog({
      appId,
      stageId,
      firstUnderwritingStageId,
    }: {
      appId: string;
      stageId?: string;
      firstUnderwritingStageId?: string | null;
    }) {
      this.dataOrchestrationLog = null;
      await this.getAllDataOrchestrationLogs({ appId });

      if (!stageId) {
        const activeLogId = this.allDataOrchestrationLogs[0]?.id;
        if (!activeLogId) {
          return;
        }
        await this.getDataOrchestrationLog({ appId, logId: activeLogId });
        return;
      }

      const logsForStage = this.getLogsForStage(
        stageId,
        firstUnderwritingStageId ?? null
      );
      const activeLogId = logsForStage[0]?.id;

      if (!activeLogId) {
        return;
      }

      await this.getDataOrchestrationLog({ appId, logId: activeLogId });
    },
    async getAllDataOrchestrationLogs({ appId }: { appId: string }) {
      try {
        this.gettingOrchestrationLogs = true;
        this.allDataOrchestrationLogs =
          await applicationsApiService.getDataOrchestrationLogs(appId);
      } catch (error) {
        // Silently ignore cancelled requests
        if ((error as { canceled?: boolean })?.canceled) {
          return;
        }
        throw error;
      } finally {
        this.gettingOrchestrationLogs = false;
      }
    },
    async getDataOrchestrationLog({
      appId,
      logId,
    }: {
      appId: string;
      logId: string;
    }) {
      try {
        this.dataOrchestrationLog =
          await applicationsApiService.getDataOrchestrationLog(appId, logId);
      } catch (error) {
        // Silently ignore cancelled requests
        if ((error as { canceled?: boolean })?.canceled) {
          return;
        }
        throw error;
      }
    },
    updateDataOrchestrationLog(log: IDataOrchestrationLog) {
      this.dataOrchestrationLog = log;
    },
    async updateDataOrchestrationMasterStatus(payload: {
      appId: string;
      logId: string;
      master_status: number;
    }) {
      await applicationsApiService.updateDataOrchestrationMasterStatus(
        payload.appId,
        payload.logId,
        payload.master_status
      );
    },
    setPhoneNumbers(data: PhoneNumbersResponse | null) {
      this.phoneNumbers = data;
    },
    updateFetchServiceStatus(
      services: (typeof EXTRA_DEAL_VIEW_REQUESTS)[number][],
      status: boolean
    ) {
      services.forEach((service) => (this.shouldFetch[service] = status));
    },
  },
});

export default useApplicationsStore;
